import { useTranslation } from "react-i18next";
import {
  Button,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icon,
  cn,
} from "@nexus/ui";
import type { PreflightScoreBand } from "../../types/preflight.types";
import { mediaKindLabelKey, type MediaKind } from "../../lib/media-type";

export type CreativeSort = "default" | "score-desc" | "score-asc" | "type";

const SORT_LABEL_KEYS: Record<CreativeSort, string> = {
  default: "preflight.creatives.sortDefault",
  "score-desc": "preflight.creatives.sortScoreDesc",
  "score-asc": "preflight.creatives.sortScoreAsc",
  type: "preflight.creatives.sortType",
};

/** Band order for the filter menu (strongest first). */
const FILTER_BANDS: PreflightScoreBand[] = [
  "STRONG",
  "NEEDS_REVIEW",
  "AT_RISK",
];

/** Media-kind order for the type filter menu (video → image → other). */
const FILTER_MEDIA: MediaKind[] = ["video", "image", "other"];

export interface CreativesToolbarProps {
  sort: CreativeSort;
  onSortChange: (sort: CreativeSort) => void;
  /** Selected bands; an empty array means "all bands" (no filter). */
  bandFilter: PreflightScoreBand[];
  onBandFilterChange: (bands: PreflightScoreBand[]) => void;
  /** Scored-creative count per band — bands with no creatives are omitted from the menu. */
  bandCounts: Record<PreflightScoreBand, number>;
  /** Selected media kinds; an empty array means "all media" (no filter). */
  mediaFilter: MediaKind[];
  onMediaFilterChange: (kinds: MediaKind[]) => void;
  /** Creative count per media kind — kinds with no creatives are omitted from the menu. */
  mediaCounts: Record<MediaKind, number>;
  /** Whether the bulk action should collapse (true) or expand (false) all cards next. */
  allExpanded: boolean;
  onToggleExpandAll: () => void;
}

/** Sort + band-filter + expand/collapse-all controls for the creative results list. */
export function CreativesToolbar({
  sort,
  onSortChange,
  bandFilter,
  onBandFilterChange,
  bandCounts,
  mediaFilter,
  onMediaFilterChange,
  mediaCounts,
  allExpanded,
  onToggleExpandAll,
}: CreativesToolbarProps) {
  const { t } = useTranslation();

  const availableBands = FILTER_BANDS.filter((band) => bandCounts[band] > 0);
  const toggleBand = (band: PreflightScoreBand) =>
    onBandFilterChange(
      bandFilter.includes(band)
        ? bandFilter.filter((b) => b !== band)
        : [...bandFilter, band],
    );

  const filterLabel =
    bandFilter.length === 0
      ? t("preflight.creatives.filterAllBands")
      : bandFilter.length === 1
        ? t(`preflight.band.${bandFilter[0]}`)
        : t("preflight.creatives.filterBandCount", {
            count: bandFilter.length,
          });

  const availableMedia = FILTER_MEDIA.filter((kind) => mediaCounts[kind] > 0);
  const toggleMedia = (kind: MediaKind) =>
    onMediaFilterChange(
      mediaFilter.includes(kind)
        ? mediaFilter.filter((k) => k !== kind)
        : [...mediaFilter, kind],
    );

  const mediaLabel =
    mediaFilter.length === 0
      ? t("preflight.creatives.filterAllMedia")
      : mediaFilter.length === 1
        ? t(mediaKindLabelKey(mediaFilter[0]!))
        : t("preflight.creatives.filterMediaCount", {
            count: mediaFilter.length,
          });

  return (
    <div className="flex items-center gap-1">
      {availableBands.length > 1 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="filter"
              className={cn(
                "gap-1.5",
                bandFilter.length > 0
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
              aria-label={t("preflight.creatives.filterAria")}
            >
              {filterLabel}
              <Icon name="chevron-down" size="xs" color="inherit" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {t("preflight.creatives.filterBandLabel")}
            </DropdownMenuLabel>
            {availableBands.map((band) => (
              <DropdownMenuCheckboxItem
                key={band}
                checked={bandFilter.includes(band)}
                onCheckedChange={() => toggleBand(band)}
                onSelect={(event) => event.preventDefault()}
              >
                {t(`preflight.band.${band}`)}
                <span className="text-muted-foreground ml-auto pl-4 tabular-nums">
                  {bandCounts[band]}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
            {bandFilter.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onBandFilterChange([])}>
                  {t("preflight.creatives.filterClear")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      {availableMedia.length > 1 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              iconStart="clapperboard"
              className={cn(
                "gap-1.5",
                mediaFilter.length > 0
                  ? "text-foreground"
                  : "text-muted-foreground",
              )}
              aria-label={t("preflight.creatives.filterMediaAria")}
            >
              {mediaLabel}
              <Icon name="chevron-down" size="xs" color="inherit" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>
              {t("preflight.creatives.filterMediaLabel")}
            </DropdownMenuLabel>
            {availableMedia.map((kind) => (
              <DropdownMenuCheckboxItem
                key={kind}
                checked={mediaFilter.includes(kind)}
                onCheckedChange={() => toggleMedia(kind)}
                onSelect={(event) => event.preventDefault()}
              >
                {t(mediaKindLabelKey(kind))}
                <span className="text-muted-foreground ml-auto pl-4 tabular-nums">
                  {mediaCounts[kind]}
                </span>
              </DropdownMenuCheckboxItem>
            ))}
            {mediaFilter.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onMediaFilterChange([])}>
                  {t("preflight.creatives.filterClear")}
                </DropdownMenuItem>
              </>
            ) : null}
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            iconStart="sliders-horizontal"
            className="text-muted-foreground gap-1.5"
            aria-label={t("preflight.creatives.sortAria")}
          >
            {t(SORT_LABEL_KEYS[sort])}
            <Icon name="chevron-down" size="xs" color="inherit" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup
            value={sort}
            onValueChange={(value) => onSortChange(value as CreativeSort)}
          >
            <DropdownMenuRadioItem value="default">
              {t("preflight.creatives.sortDefault")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="score-desc">
              {t("preflight.creatives.sortScoreDesc")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="score-asc">
              {t("preflight.creatives.sortScoreAsc")}
            </DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="type">
              {t("preflight.creatives.sortType")}
            </DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        iconStart={allExpanded ? "minimize-2" : "maximize-2"}
        className="text-muted-foreground"
        onClick={onToggleExpandAll}
      >
        {t(
          allExpanded
            ? "preflight.creatives.collapseAll"
            : "preflight.creatives.expandAll",
        )}
      </Button>
    </div>
  );
}
